import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { ControlApiConfiguration } from '../apiConfiguration';
import { StorageService } from '../../storage.service';

/** Answer for a user whose permissions have not been fetched yet - see getPermissionSet(). */
const NO_PERMISSIONS = new Set<string>();

@Injectable()
export class AuthorizeService {

    private actionUrl: string;
    private url: string;
    public permissions;

    private readonly permissionsChangedSubject = new Subject<void>();

    /**
     * Emits when the stored permission list is replaced (login, facility switch) or cleared
     * (logout). Anything that pre-computes something out of the user's permissions has to drop it
     * then - see MenuPermissionService and MenuSearchService's row memo. It matters because the
     * menu is reachable before the permissions are: AuthService.setUser() assigns `user`, which is
     * all AuthGuard checks, and only then awaits setPermission(), so a cache built inside that
     * window was built against no permissions at all.
     */
    readonly permissionsChanged: Observable<void> = this.permissionsChangedSubject.asObservable();

    /**
     * The user's permission names, parsed out of secure storage once and held until that list
     * changes. Reading it is not cheap - every secureStorage.getItem() SHA-256s the key and AES
     * decrypts the whole list (see StorageService) before the JSON.parse - and hasPermission() is
     * called per menu node when the sidebar is built, per row when the shell filters its search
     * results, and on every `hasPermission` template binding.
     */
    private permissionSet: Set<string> | null = null;

    constructor(private _http: HttpClient
        , private configuration: ControlApiConfiguration
        , private storageService: StorageService) {
        this.actionUrl = configuration.server;
        this.url = this.actionUrl + configuration.rolePermission.userPermissions;
    }

    async getUserPermission() {
        const response = await this._http.get<any>(this.url, { withCredentials: true }).toPromise();
        this.storageService.secureStorage.setItem('permissions', JSON.stringify(response));
        this.invalidatePermissions();
        return response;
    }

    hasPermission = (permission: string): boolean => {
        return this.getPermissionSet().has(permission);
    }

    clearPermissions() {
        const cleared = this.storageService.secureStorage.clear();
        this.invalidatePermissions();
        return cleared;
    }

    /**
     * Nothing is memoized until there is something to memoize: an absent or empty stored list
     * means the permissions call has not answered yet (or the user just logged out), and caching
     * the empty set for that would pin "no permissions" in place for the rest of the session.
     * An empty list still answers false to everything, exactly as the previous findIndex did.
     */
    private getPermissionSet(): Set<string> {
        if (this.permissionSet) {
            return this.permissionSet;
        }
        const stored = this.storageService.secureStorage.getItem('permissions');
        const permissions = stored ? JSON.parse(stored) : null;
        if (!Array.isArray(permissions) || permissions.length === 0) {
            return NO_PERMISSIONS;
        }
        this.permissionSet = new Set<string>(permissions);
        return this.permissionSet;
    }

    private invalidatePermissions(): void {
        this.permissionSet = null;
        this.permissionsChangedSubject.next();
    }
}

